<script lang="ts">
  import Calendar32 from "carbon-icons-svelte/lib/Calendar32";
  import { fly } from "svelte/transition";
  import { quintOut } from "svelte/easing";
  import { isBefore, isSameDay, startOfDay } from "date-fns";

  import DatePicker from "../DatePicker";
  import Button from "../Button";
  import DateInput from "../DateInput";
  import Switch from "../Switch";

  import {
    yesterday,
    lastBusinessWeek,
    lastWeek,
    lastMonth,
    today,
  } from "../util/dates";
  import type { DateRange } from "../util/dates";
  import { noop } from "../util/general";

  type NonRangeOnApply = (dateValue: Date | null) => void;
  type RangeOnApply = (
    startDateValue: Date | null,
    endDateValue: Date | null,
    includeWeekends: boolean | false
  ) => void;
  export let onApply: NonRangeOnApply | RangeOnApply = noop;
  export let onCancel: () => void = noop;
  export let isDateRange: boolean = false;
  export let dateValue: Date | null = today;
  export let startDateValue: Date | null = today;
  export let endDateValue: Date | null = today;
  export let markedDates: Date[] = [];
  export let disableFuture: boolean = false;
  export let includeWeekends = false;

  let isError: boolean = false;
  let submitEnabled: boolean = false;
  let yesterdaySelected: boolean = false;
  let lastWeekSelected: boolean = false;
  let lastMonthSelected: boolean = false;
  let lastYearSelected: boolean = false;

  $: isError =
    isDateRange &&
    endDateValue !== null &&
    startDateValue !== null &&
    isBefore(startOfDay(endDateValue), startOfDay(startDateValue));

  $: submitEnabled = isDateRange
    ? !isError && startDateValue !== null && endDateValue !== null
    : dateValue !== null;

  $: yesterdaySelected =
    isDateRange &&
    startDateValue !== null &&
    endDateValue !== null &&
    isSameDay(startDateValue, yesterday.start) &&
    isSameDay(endDateValue, yesterday.end);

  $: lastWeekSelected =
    isDateRange &&
    startDateValue !== null &&
    endDateValue !== null &&
    isSameDay(startDateValue, lastWeek.start) &&
    isSameDay(lastWeek.end, endDateValue);

  $: lastBusinessWeekSelected =
    isDateRange &&
    startDateValue !== null &&
    endDateValue !== null &&
    isSameDay(startDateValue, lastBusinessWeek.start) &&
    isSameDay(lastBusinessWeek.end, endDateValue);

  const onSwitchClick = (checked) => {
    // When Last week is selected we need to update the dates when includeWeekends is modified
    if(lastWeekSelected || lastBusinessWeekSelected) {
      return setDateRange(checked ? lastWeek : lastBusinessWeek)
    }
  }

  $: lastMonthSelected =
    isDateRange &&
    startDateValue !== null &&
    endDateValue !== null &&
    isSameDay(startDateValue, lastMonth.start) &&
    isSameDay(lastMonth.end, endDateValue);

  let datePickerIsOpen: boolean = false;

  const setDateRange = (dr: DateRange): (() => void) => () => {
    startDateValue = dr.start;
    endDateValue = dr.end;
  };

  const onApply_ = isDateRange
    ? () => onApply(startDateValue, endDateValue, includeWeekends)
    : () => onApply(dateValue);
</script>

<style>
  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: min-content;
    border-radius: 8px;
    box-shadow: 1px 1px 4px 0px #00000040;
    padding: 2em;
    transition: width 125ms ease-in, height 125ms ease-in 50ms;
    background: white;
  }
  .container-normal {
    width: 13.27em;
    height: 18.5em;
  }
  .container-expanded {
    width: 20em;
    height: 28.5em;
  }
  .input-section {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 4.5em;
  }
  .input-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }
  .icon-wrapper {
    /* Hardcoded pixels since icons don't scale */
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: -1em;
    margin-left: 0.75em;
    cursor: pointer;
  }
  .datepicker-button-container {
    padding-top: 2em;
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
  #date-range-presets {
    margin-top: 1.75em;
    width: 100%;
  }
  .date-range-preset-row {
    display: flex;
    justify-content: space-between;
  }
  .date-range-preset-row > div {
    cursor: pointer;
    padding: 0.25em 0.5em 0.25em 0.5em;
    border-radius: 0.25em;
  }
  .date-range-preset-row > div:active {
    transform: scale(0.95);
  }
  .date-range-preset-row > div:hover {
    background-color: var(--theme-accent);
    color: white;
    transition-property: background-color;
    transition-duration: 150ms;
    transition-timing-function: ease-in-out;
  }
  .preset-selected {
    background-color: var(--theme-accent);
    color: white;
  }
  #apply-button-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
  #apply-button-wrapper {
    margin-top: 2em;
    height: 1.75em;
    width: 6em;
  }
  #error-message {
    width: 100%;
    height: 1em;
    padding-bottom: 1.75em;
    font-size: 1.125em;
    text-align: left;
  }
  .notIsError {
    color: black;
    font-weight: 600;
  }
  .isError {
    color: #be6984;
    font-weight: 600;
  }
  .switch-container {
    margin: 1em 0.2em 0;
    display: flex;
    justify-content: space-between;
  }
  .switch-container span{
    color: #141414;
    font-weight: 600;
  }
</style>

<div
  class="container"
  class:container-normal={!datePickerIsOpen}
  class:container-expanded={datePickerIsOpen}>
  {#if !datePickerIsOpen}
    <div id="error-message" class:isError class:notIsError={!isError}>
      {isError ? 'Start date is after end date' : isDateRange ? 'Enter Date Range' : 'Enter Date'}
    </div>
    <div class="input-section">
      {#if !isDateRange}
        <DateInput label="Date" bind:dateInputValue={dateValue} />
      {:else}
        <div class="input-wrapper">
          <DateInput
            {isError}
            label="Start"
            bind:dateInputValue={startDateValue} />
          <DateInput {isError} label="End" bind:dateInputValue={endDateValue} />
        </div>
      {/if}
      <div
        class="icon-wrapper"
        class:icon-bump-down={true}
        on:click={() => {
          dateValue = null;
          startDateValue = null;
          endDateValue = null;
          datePickerIsOpen = true;
        }}>
        <Calendar32 />
      </div>
    </div>
    {#if isDateRange}
      <div id="date-range-presets-divider" />
      <div id="date-range-presets">
        <div class="date-range-preset-row">
          <div
            class:preset-selected={yesterdaySelected}
            on:click={setDateRange(yesterday)}>
            Yesterday
          </div>
          <div
            class:preset-selected={lastWeekSelected || lastBusinessWeekSelected}
            on:click={setDateRange(includeWeekends ? lastWeek : lastBusinessWeek)}>
            Last Week
          </div>
        </div>
        <div class="date-range-preset-row" style="margin-top: 0.125em;">
          <div
            class:preset-selected={lastMonthSelected}
            on:click={setDateRange(lastMonth)}>
            Last Month
          </div>
        </div>
        <div class="switch-container">
          <Switch bind:checked={includeWeekends} onSwitchClick={onSwitchClick}/>
          <span>Include Weekends</span>
        </div>
      </div>
    {/if}
    <div id="apply-button-container">
      <div id="apply-button-wrapper">
        <Button fillParent type="secondary" onClick={onCancel}>Back</Button>
      </div>
      <div id="apply-button-wrapper">
        <Button
          type={!submitEnabled ? 'disabled' : 'primary'}
          fillParent
          onClick={onApply_}>
          Apply
        </Button>
      </div>
    </div>
  {:else}
    <div
      in:fly={{ delay: 200, duration: 75, x: 0, opacity: 0.0, easing: quintOut }}>
      {#if !isDateRange}
        <DatePicker showBorder={false} bind:selectedDate={dateValue} />
      {:else}
        <DatePicker
          showBorder={false}
          isDateRange={true}
          bind:selectedDateRangeStart={startDateValue}
          bind:selectedDateRangeEnd={endDateValue}
          {markedDates}
          {disableFuture}
        />
      {/if}
      <div class="datepicker-button-container">
        <Button
          type="secondary"
          onClick={() => {
            datePickerIsOpen = false;
          }}>
          Back
        </Button>
        <Button
          type={!submitEnabled ? 'disabled' : 'primary'}
          onClick={onApply_}>
          Apply
        </Button>
      </div>
    </div>
  {/if}
</div>
